import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Archive, ExternalLink, FileSignature, FileSpreadsheet, Loader2, RefreshCw, Search, Trash2, X } from "lucide-react";
import { ascii } from "../../lib/contractRegister";
import { api } from "../../lib/api";
import ExportButton from "./ExportButton";
import { exportName, exportPath } from "../../lib/exportFile";
import { downloadBlob } from "../../lib/downloadBlob";
import { hasFilters, REGISTER_BUCKETS, registerParams, rowAttention, shortStatus } from "../../lib/contractRegister";
import { contractStatusOf, TONE } from "../../lib/contractStatus";
import { todoTabBadge, urgentContractIds } from "../../lib/contractTodo";
import ContractCard, { BTN_QUIET, errorDetail, fmtDateTime } from "./ContractCard";
import ContractBatchDialog from "./ContractBatchDialog";
import ContractTodo from "./ContractTodo";
import TraineeRegisterDialog from "./TraineeRegisterDialog";
import Pagination from "./Pagination";
import { contractTitle, isFpedContract } from "../../lib/regime";

const PAGE_SIZE = 50;
const THIS_YEAR = new Date().getFullYear();
const YEARS = [THIS_YEAR, THIS_YEAR - 1];
const EMPTY = { status: "", group_id: "", year: "", sent_from: "", sent_to: "", q: "", probes: false, regime: "" };

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("ro-RO");
};

/**
 * The contracts register: every number ever issued, and where each contract
 * stands. Owner and admin only — the documents behind it carry CNPs.
 *
 * `?contract=<id>` opens a contract's drawer, which is how the enrollment
 * drawer links here.
 *
 * Two tabs: the register (the default) and "De rezolvat", the cases that
 * need a person. The second tab's badge counts the urgent ones, so they show
 * from the register too. `?vedere=de-rezolvat` keeps the tab across reloads.
 */
export default function ContractsTab({ onOpenEnrollment, isOwner = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get("contract");
  const view = searchParams.get("vedere") === "de-rezolvat" ? "todo" : "register";
  const setView = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "todo") params.set("vedere", "de-rezolvat"); else params.delete("vedere");
    setSearchParams(params, { replace: true });
  };
  const [filters, setFilters] = useState(EMPTY);
  const [qDraft, setQDraft] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const params = useMemo(() => registerParams(filters), [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/contracts", { params: { ...params, page, page_size: PAGE_SIZE } });
      setData(r.data);
    } catch (err) {
      setData({ error: errorDetail(err, "Registrul nu a putut fi încărcat.") });
    } finally {
      setLoading(false);
    }
  }, [params, page]);

  useEffect(() => { load(); }, [load]);

  const [todo, setTodo] = useState(null);
  const [todoLoading, setTodoLoading] = useState(false);
  const [todoCategory, setTodoCategory] = useState("");
  const [batchOpen, setBatchOpen] = useState(false);
  const loadTodo = useCallback(async () => {
    setTodoLoading(true);
    try {
      setTodo((await api.get("/admin/contracts/todo")).data);
    } catch (err) {
      setTodo({ error: errorDetail(err, "Lista „De rezolvat” nu a putut fi încărcată.") });
    } finally {
      setTodoLoading(false);
    }
  }, []);
  useEffect(() => { loadTodo(); }, [loadTodo]);
  const urgentIds = useMemo(() => urgentContractIds(todo?.items), [todo]);
  const reloadAll = useCallback(() => { load(); loadTodo(); }, [load, loadTodo]);
  const badge = todoTabBadge(todo);

  useEffect(() => {
    api.get("/admin/contracts/groups", { params: { probes: filters.probes } })
      .then((r) => setGroups(r.data.groups || []))
      .catch(() => setGroups([]));
  }, [filters.probes]);

  // Search as you type, without a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => (f.q === qDraft ? f : { ...f, q: qDraft }));
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [qDraft]);

  const set = (key, value) => { setFilters((f) => ({ ...f, [key]: value })); setPage(1); };
  const clear = () => { setFilters((f) => ({ ...EMPTY, probes: f.probes })); setQDraft(""); setPage(1); };

  const openContract = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("contract", id); else next.delete("contract");
    setSearchParams(next, { replace: !id });
  };

  const exportCsv = (format) => downloadBlob(exportPath("/admin/contracts/export.csv", params, format),
    exportName("registru-contracte", format), setExporting);

  const [zipping, setZipping] = useState(false);
  const exportZip = async () => {
    const group = groups.find((g) => g.id === filters.group_id);
    if (!window.confirm(
      `Descarci contractele semnate ale grupei „${group?.name || filters.group_id}”, cu certificatele de semnare? `
      + "Documentele conțin CNP-ul participanților: păstrează fișierul doar pentru dosarul de acreditare. "
      + "Descărcarea rămâne în jurnalul de audit.",
    )) return;
    setZipping(true);
    try {
      const r = await api.get("/admin/contracts/export.zip", { params: { group_id: filters.group_id }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `contracte-${ascii(group?.name || "grupa")}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (err) {
      let msg = "Exportul ZIP nu a reușit.";
      try { msg = JSON.parse(await err?.response?.data?.text())?.detail || msg; } catch { /* keep the default */ }
      toast.error(msg);
    } finally {
      setZipping(false);
    }
  };

  const [registerOpen, setRegisterOpen] = useState(false);

  const [purging, setPurging] = useState(false);
  const purgeProbes = async () => {
    let s;
    try {
      s = (await api.get("/admin/contracts/probes/summary")).data;
    } catch {
      toast.error("Nu am putut citi probele.");
      return;
    }
    const open = s.open_at_esemneaza ? ` ${s.open_at_esemneaza} sunt încă deschise la eSemneaza și vor fi anulate.` : "";
    if (!window.confirm(`Ștergi definitiv ${s.total} ${s.total === 1 ? "contract de probă" : "contracte de probă"}, `
      + `cu PDF-urile lor din arhivă?${open} Contractele reale nu sunt atinse.`)) return;
    setPurging(true);
    try {
      const r = await api.post("/admin/contracts/probes/purge");
      toast.success(`Am șters ${r.data.deleted} ${r.data.deleted === 1 ? "probă" : "probe"}.`);
      await load();
    } catch (err) {
      toast.error(errorDetail(err, "Probele nu au putut fi șterse."));
    } finally {
      setPurging(false);
    }
  };

  const counts = data?.counts || {};
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div data-testid="contracts-tab">
      <div className="flex gap-1 border-b border-[#E5E7EB]" role="tablist" aria-label="Contracte">
        {[
          { key: "register", label: "Registru" },
          { key: "todo", label: "De rezolvat" },
        ].map((t) => {
          const on = view === t.key;
          return (
            <button key={t.key} type="button" role="tab" aria-selected={on} onClick={() => setView(t.key)}
              className={`-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#965EA7] ${
                on ? "border-[#965EA7] text-[#373A45]" : "border-transparent text-[#6B7280] hover:text-[#373A45]"}`}
              data-testid={`contracts-view-${t.key}`}>
              {t.label}
              {t.key === "todo" && badge && (
                <span className={`min-w-[20px] rounded-full px-1.5 py-0.5 text-center text-[11px] font-semibold tabular-nums ${
                  badge.urgent ? "bg-[#C62828] text-white" : "bg-[#EDEEF0] text-[#6B7280]"}`}
                  title={badge.title} data-testid="contracts-todo-badge">
                  {badge.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {view === "todo" && (
        <div className="mt-4" role="tabpanel" aria-label="De rezolvat">
          <ContractTodo data={todo} loading={todoLoading} onReload={reloadAll}
            category={todoCategory} onCategory={setTodoCategory}
            onOpenEnrollment={onOpenEnrollment} onOpenContract={openContract}
            onBatch={() => setBatchOpen(true)} />
        </div>
      )}

      {view === "register" && (<div role="tabpanel" aria-label="Registru">
      <p className="mt-4 max-w-3xl text-sm text-[#6B7280] leading-relaxed">
        Registrul contractelor de formare. Fiecare număr emis are un rând, inclusiv contractele anulate, refuzate
        sau netrimise. Documentele semnate conțin CNP-ul și se descarcă din fișa contractului.
      </p>

      {/* Counters, each a filter */}
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5" data-testid="contracts-counters">
        {REGISTER_BUCKETS.map((b) => {
          const active = filters.status === b.key;
          const n = counts[b.key] ?? "—";
          const alert = b.key === "waiting_fped" && counts[b.key] > 0;
          return (
            <button key={b.key} type="button" onClick={() => set("status", active ? "" : b.key)}
              aria-pressed={active}
              className={`rounded-xl border px-3 py-2.5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#965EA7] ${
                active ? "border-[#965EA7] bg-[#F5F0F8]" : "border-[#E5E7EB] bg-white hover:bg-[#F5F6F6]"}`}
              data-testid={`contracts-counter-${b.key}`}>
              <div className="flex items-center gap-1.5 text-2xl font-semibold tabular-nums text-[#373A45]">
                {n}
                {alert && <AlertTriangle size={16} className="text-[#B45309]" aria-label="De verificat" />}
              </div>
              <div className="mt-0.5 text-xs text-[#6B7280]">{b.label}</div>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="mt-4 flex flex-wrap items-end gap-2">
        <label className="relative min-w-[220px] flex-1">
          <span className="sr-only">Caută</span>
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <input value={qDraft} onChange={(e) => setQDraft(e.target.value)} placeholder="Număr, nume sau email"
            className="w-full rounded-full border border-[#E5E7EB] bg-white py-2 pl-9 pr-3 text-sm focus:border-[#965EA7] focus:outline-none"
            data-testid="contracts-search" />
        </label>
        <select value={filters.group_id} onChange={(e) => set("group_id", e.target.value)}
          className="rounded-full border border-[#E5E7EB] bg-white px-3 py-2 text-sm" aria-label="Grupă"
          data-testid="contracts-group-filter">
          <option value="">Toate grupele</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>{g.name}{g.start_date ? ` · ${fmtDate(g.start_date)}` : ""} ({g.count})</option>
          ))}
        </select>
        <select value={filters.regime} onChange={(e) => set("regime", e.target.value)}
          className="rounded-full border border-[#E5E7EB] bg-white px-3 py-2 text-sm" aria-label="Regim"
          data-testid="contracts-regime-filter" title="Contractul cursului acreditat sau contractul Curs FPED">
          <option value="">Regim: toate</option>
          <option value="acreditat">Acreditat MEC</option>
          <option value="fped">Curs FPED</option>
        </select>
        <select value={filters.year} onChange={(e) => set("year", e.target.value)}
          className="rounded-full border border-[#E5E7EB] bg-white px-3 py-2 text-sm" aria-label="An">
          <option value="">Toți anii</option>
          {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-[#6B7280]">
          Trimis între
          <input type="date" value={filters.sent_from} onChange={(e) => set("sent_from", e.target.value)}
            className="rounded-full border border-[#E5E7EB] bg-white px-2.5 py-1.5 text-sm text-[#373A45]" />
          și
          <input type="date" value={filters.sent_to} onChange={(e) => set("sent_to", e.target.value)}
            className="rounded-full border border-[#E5E7EB] bg-white px-2.5 py-1.5 text-sm text-[#373A45]" />
        </label>
        {hasFilters(filters) && (
          <button type="button" onClick={clear} className={BTN_QUIET} data-testid="contracts-clear">
            <X size={14} /> Șterge filtrele
          </button>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-[#6B7280]">
            <input type="checkbox" checked={filters.probes} onChange={(e) => set("probes", e.target.checked)}
              className="h-4 w-4 accent-[#965EA7]" data-testid="contracts-probes-toggle" />
            Doar probele
          </label>
          <button type="button" onClick={reloadAll} disabled={loading} className={BTN_QUIET} aria-label="Reîncarcă">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
          {!filters.probes && (
            <button type="button" onClick={() => setRegisterOpen(true)} className={BTN_QUIET}
              title="Registrul unic de evidență a formabililor, în Excel. Conține CNP-uri."
              data-testid="contracts-trainee-register">
              <FileSpreadsheet size={14} /> Registru formabili
            </button>
          )}
          {filters.group_id && !filters.probes && (
            <button type="button" onClick={exportZip} disabled={zipping} className={BTN_QUIET}
              title="Contractele semnate ale grupei, cu certificatele, pentru dosarul de acreditare. Conțin CNP-uri."
              data-testid="contracts-export-zip">
              {zipping ? <Loader2 size={14} className="animate-spin" /> : <Archive size={14} />} ZIP grupă
            </button>
          )}
          <ExportButton onExport={exportCsv} busy={exporting} disabled={!total} className={BTN_QUIET}
            title="Registrul filtrat, fără CNP" testid="contracts-export" />
        </div>
      </div>

      {filters.probes && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-[#FDF3E7] px-3 py-2 text-xs text-[#8a6a1a]">
          <span>Vezi doar contractele de probă (seria PROBA-), fără valoare contractuală.</span>
          {isOwner && total > 0 && (
            <button type="button" onClick={purgeProbes} disabled={purging}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#9b1f2c]/40 bg-white px-3 py-1 font-semibold text-[#9b1f2c] hover:bg-[#FDECEC] disabled:opacity-50"
              data-testid="contracts-purge-probes">
              {purging ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Șterge toate probele
            </button>
          )}
        </div>
      )}

      {/* The register */}
      <div className="mt-4 overflow-x-auto rounded-2xl border border-[#E5E7EB] bg-white">
        <table className="w-full text-sm" data-testid="contracts-table">
          <thead className="bg-[#EDEEF0] text-left text-[#373A45]">
            <tr>
              {["Nr. contract", "Participant", "Grupă", "Status", "Trimis", "Semnat complet", "Arhivat"].map((h) => (
                <th key={h} className="whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-widest">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.error && <tr><td colSpan={7} className="p-6 text-center text-[#9b1f2c]">{data.error}</td></tr>}
            {!data && <tr><td colSpan={7} className="p-6 text-center text-[#6B7280]">Se încarcă…</td></tr>}
            {data && !data.error && data.items.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-[#6B7280]">
                {hasFilters(filters) ? "Niciun contract pentru filtrele curente." : "Încă nu a fost trimis niciun contract."}
              </td></tr>
            )}
            {data?.items?.map((c) => {
              const st = shortStatus(c.status);
              const attention = rowAttention(c);
              const urgent = urgentIds.has(c.id);
              return (
                <tr key={c.id} onClick={() => openContract(c.id)}
                  className="cursor-pointer border-t border-[#E5E7EB] hover:bg-[#F5F6F6]" data-testid={`contract-row-${c.id}`}>
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-[13px] font-semibold text-[#373A45]">
                    <span className="inline-flex items-center gap-1.5">
                      {urgent && <span className="h-2 w-2 rounded-full bg-[#C62828]" title="Urgent: grupa începe în mai puțin de 48 h"
                        aria-label="Urgent" data-testid={`contract-urgent-${c.id}`} />}
                      {c.number || "—"}
                      {isFpedContract(c) && (
                        <span className="rounded-full bg-[#3F8EBA]/10 px-1.5 py-0.5 font-sans text-[9px] font-bold uppercase tracking-widest text-[#2f6d90]"
                          data-testid={`contract-regime-${c.id}`}>Curs FPED</span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#373A45]">{c.participant_name || "—"}</div>
                    <div className="text-[11px] text-[#6B7280]">{c.participant_email}</div>
                  </td>
                  <td className="px-4 py-3 text-[#373A45]">
                    {c.group_name || <span className="text-[#6B7280]">—</span>}
                    {c.group_start_date && <div className="text-[11px] text-[#6B7280]">din {fmtDate(c.group_start_date)}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${TONE[st.tone]}`}>
                      {st.label}
                    </span>
                    {attention && (
                      <div className="mt-1 flex max-w-[220px] items-start gap-1 text-[11px] text-[#9b1f2c]">
                        <AlertTriangle size={12} className="mt-px shrink-0" /> <span className="line-clamp-2">{attention}</span>
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-[#6B7280] tabular-nums">{fmtDate(c.sent_at)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-[#6B7280] tabular-nums">{fmtDate(c.completed_at)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-[#6B7280] tabular-nums">{c.archived_at ? "da" : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Pagination page={page} pageSize={PAGE_SIZE} total={total} totalPages={totalPages}
          hasPrev={page > 1} hasNext={page < totalPages} onPageChange={setPage} testid="contracts-pagination" />
      </div>

      </div>)}

      {registerOpen && (
        <TraineeRegisterDialog group={groups.find((g) => g.id === filters.group_id) || null}
          onClose={() => setRegisterOpen(false)} />
      )}

      {batchOpen && <ContractBatchDialog onClose={() => setBatchOpen(false)} onSent={reloadAll} />}

      {openId && (
        <ContractDrawer contractId={openId} onClose={() => openContract(null)} onChanged={reloadAll}
          onOpenContract={openContract} onOpenEnrollment={onOpenEnrollment} />
      )}
    </div>
  );
}

function ContractDrawer({ contractId, onClose, onChanged, onOpenContract, onOpenEnrollment }) {
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/contracts/${contractId}`);
      setDetail(r.data.contract);
    } catch (err) {
      setDetail({ error: errorDetail(err, "Contractul nu a putut fi încărcat.") });
    }
  }, [contractId]);

  useEffect(() => { setDetail(null); load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="contract-drawer">
      <aside role="dialog" aria-modal="true" aria-label="Fișa contractului" onClick={(e) => e.stopPropagation()}
        className="absolute right-0 top-0 h-full w-full max-w-xl overflow-y-auto bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#965EA7]">
              <FileSignature size={14} /> {contractTitle(isFpedContract(detail))}
            </div>
            <h2 className="mt-1 font-mono text-lg font-semibold text-[#373A45]">{detail?.number || "…"}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-[#6B7280] hover:text-[#373A45]" aria-label="Închide"
            data-testid="contract-drawer-close">
            <X size={18} />
          </button>
        </div>

        {!detail && (
          <div className="mt-4 flex items-center gap-2 text-sm text-[#6B7280]">
            <Loader2 size={14} className="animate-spin" /> Se încarcă…
          </div>
        )}
        {detail?.error && <p className="mt-4 text-sm text-[#9b1f2c]">{detail.error}</p>}

        {detail && !detail.error && (
          <>
            <dl className="mt-4 grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5 text-sm">
              <dt className="text-[#6B7280]">Participant</dt>
              <dd className="text-[#373A45]">
                <span className="font-medium">{detail.participant_name || "—"}</span>
                {detail.participant_email && <span className="text-[#6B7280]"> · {detail.participant_email}</span>}
              </dd>
              <dt className="text-[#6B7280]">Grupă</dt>
              <dd className="text-[#373A45]">
                {detail.group_name || "—"}
                {detail.group_start_date && <span className="text-[#6B7280]"> · din {fmtDate(detail.group_start_date)}</span>}
              </dd>
              <dt className="text-[#6B7280]">Text contract</dt>
              <dd className="text-[#373A45]">versiunea {detail.template_version || "—"}</dd>
              {detail.sandbox && (<><dt className="text-[#6B7280]">Tip</dt><dd className="text-[#8a6a1a]">probă, fără valoare contractuală</dd></>)}
            </dl>

            {detail.enrollment_id && onOpenEnrollment && (
              <button type="button" onClick={() => onOpenEnrollment(detail.enrollment_id)}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#965EA7] hover:underline"
                data-testid="contract-open-enrollment">
                <ExternalLink size={14} /> Deschide înscrierea
              </button>
            )}

            <ContractCard contract={detail} testIdPrefix="register-contract"
              onChanged={async () => { await load(); onChanged?.(); }} />
            {detail.archive_error && (
              <p className="mt-2 text-xs text-[#9b1f2c]">Arhivarea a eșuat ({detail.archive_error}). „Actualizează statusul” o reîncearcă.</p>
            )}

            <h3 className="mt-6 text-xs font-semibold uppercase tracking-widest text-[#6B7280]">Istoric</h3>
            <ol className="mt-2 border-l border-[#E5E7EB] pl-4" data-testid="contract-history">
              {detail.history.map((h, i) => (
                <li key={`${h.at}-${i}`} className="relative pb-3 text-sm">
                  <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-[#965EA7]" aria-hidden="true" />
                  <div className="text-[#373A45]">{h.label}</div>
                  <div className="text-[11px] text-[#6B7280] tabular-nums">
                    {fmtDateTime(h.at)}{h.by ? ` · ${h.by}` : ""}
                  </div>
                </li>
              ))}
            </ol>

            {detail.other_contracts?.length > 0 && (
              <>
                <h3 className="mt-4 text-xs font-semibold uppercase tracking-widest text-[#6B7280]">
                  Alte contracte ale aceluiași participant
                </h3>
                <ul className="mt-2 grid gap-1.5">
                  {detail.other_contracts.map((o) => {
                    const st = contractStatusOf(o);
                    return (
                      <li key={o.id}>
                        <button type="button" onClick={() => onOpenContract(o.id)}
                          className="flex w-full items-center justify-between gap-2 rounded-lg border border-[#E5E7EB] px-3 py-2 text-left text-sm hover:bg-[#F5F6F6]">
                          <span className="font-mono font-semibold text-[#373A45]">
                            {o.number || "—"}{o.sandbox ? " (probă)" : ""}
                          </span>
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${TONE[st.tone]}`}>{st.label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </>
        )}
      </aside>
    </div>
  );
}
